import React, { useState } from 'react'
import { DataViewContext } from '@pushly/aqe/lib/components/data-view/context'
import { DataView } from '@pushly/aqe/lib/components/data-view/data-view'
import { Button, Table } from 'antd'
import { DataSourceResponse, DataViewProps } from '@pushly/aqe/lib/components/data-view/types'
import { DomainSecret } from '@pushly/models/lib/structs/domain-secrets/domain-secret'
import { ApiVersion } from '../../enums/api-version.enum'
import { DomainSecretsService } from '../../services/domain-secrets.service'
import { useService } from '@pushly/aqe/lib/hooks'
import { useDataViewReducer } from '@pushly/aqe/lib/components/data-view/reducer'
import moment from 'moment/moment'
import { SHORT_DATE_FORMAT } from '../../constants'
import { DomainSecretsDrawer } from './domain-secrets-drawer'
import { SecretsModel } from '../../models/domain/secrets.model'
import { ArchiveSecretModal } from '@pushly/aqe/lib/components/secrets/archive-secret-modal'
import { SecretsActionColumnView } from '@pushly/aqe/lib/components/secrets/secrets-action-column-view'
import { secretTypeLabels } from './domain-secret-labels'
import { simpleNotification } from '@pushly/aqe/lib/utils'
import { DomainSecretType } from '@pushly/models/lib/enums/domain-secret-type'
import { IApiResponse } from '@pushly/aqe/lib/interfaces'
import { UpdateDomainSecretRequestDto } from '@pushly/models/lib/structs/domain-secrets/update-domain-secret-request.dto'
import { CreateDomainSecretRequestDto } from '@pushly/models/lib/structs/domain-secrets/create-domain-secret-request.dto'
import { FeatureEmptyState } from '../icons/feature-icon'

const SECRET_VALIDATION_MESSAGE = 'Please check your secret details and try again.'

export const DomainSecretsView = (props: { id: number }) => {
    const { id } = props
    const [createButtonDisabled, setCreateButtonDisabled] = useState(false)
    const [showCreateSecretModal, setShowCreateSecretModal] = useState(false)
    const [showArchiveModal, setShowArchiveModal] = useState<DomainSecret | undefined>(undefined)
    const [editSecret, setEditSecret] = useState<DomainSecret | undefined>(undefined)

    const apiSecretsService = useService(DomainSecretsService)

    const [context, dispatch, requestDataSourceReload] = useDataViewReducer<DomainSecret>({
        id: `domain-secrets`,
        defaultPageSize: 10,
    })

    const handleNewSecretClick = () => {
        setShowCreateSecretModal(true)
    }

    const fetchDomainSecrets: DataViewProps<DomainSecret>['dataSourceLoader'] = async (filters, tableState) => {
        const page = tableState.pagination?.current ?? 1
        const limit = tableState.pagination?.pageSize ?? 10

        const apiOptions = {
            showLoadingScreen: true,
            cancellationKey: 'domain-secrets.fetch',
            version: ApiVersion.V3,
            query: { page, limit },
        }

        const res = await apiSecretsService.fetchAll(id, apiOptions)
        let dataViewResponse: DataSourceResponse = {
            meta: {},
            cancelled: false,
            ...res,
            data: [],
        }

        if (!res.cancelled) {
            if (res.ok && res.data) {
                dataViewResponse.data = res.data.map((data) => ({ data }))
            } else if (res.error) {
                console.warn(res.error)
            }
        }
        return dataViewResponse
    }

    const handleSubmitSecret = async (secret: SecretsModel) => {
        let username: string | undefined
        let password: string | undefined
        let value: string = secret.value ?? ''

        if (secret.type === DomainSecretType.USERPASS) {
            try {
                const parsed = JSON.parse(secret.value || '{}')
                const isObject = parsed && typeof parsed === 'object' && !Array.isArray(parsed)
                username = isObject && typeof parsed.username === 'string' ? parsed.username : ''
                password = isObject && typeof parsed.password === 'string' ? parsed.password : ''
            } catch (err) {
                username = ''
                password = ''
            }

            value = ''
        }

        const isEdit = !!editSecret?.id

        let res: IApiResponse<any>

        if (isEdit) {
            const payload = UpdateDomainSecretRequestDto.from({ value, username, password })

            try {
                payload.validate()
            } catch (err: any) {
                simpleNotification('error', SECRET_VALIDATION_MESSAGE)
                return { ok: false }
            }

            res = await apiSecretsService.updateSecret(id, editSecret!.id!, payload, {
                showLoadingScreen: true,
                cancellationKey: 'domain-secrets.update',
            })
        } else {
            const payload = CreateDomainSecretRequestDto.from({
                alias: secret.alias,
                type: secret.type,
                value,
                username,
                password,
            })

            try {
                payload.validate()
            } catch (err: any) {
                simpleNotification('error', SECRET_VALIDATION_MESSAGE)
                return { ok: false }
            }

            res = await apiSecretsService.create(id, payload, {
                showLoadingScreen: true,
                cancellationKey: 'domain-secrets.create',
                version: ApiVersion.V3,
            })
        }

        if (res.ok) {
            requestDataSourceReload()
            setShowCreateSecretModal(false)
            setEditSecret(undefined)
            let message = isEdit
                ? `${secret.alias} value updated successfully`
                : `${secret.alias} has been created successfully`
            simpleNotification('success', message)
        }

        return res
    }

    const handleDeleteSecret = async () => {
        if (!showArchiveModal?.id) {
            return
        }

        const res = await apiSecretsService.deleteSecret(id, showArchiveModal.id, {
            showLoadingScreen: true,
            cancellationKey: 'domain-secrets.delete',
            version: ApiVersion.V3,
        })

        if (res.ok) {
            setShowArchiveModal(undefined)
            requestDataSourceReload()
            simpleNotification('success', 'Secret deleted successfully')
        }
    }

    return (
        <>
            <DataViewContext.Provider value={{ state: context, dispatch }}>
                <DataView
                    dataSourceLoader={fetchDomainSecrets}
                    hideRefresh={true}
                    hideAutoRefreshOptions={true}
                    actions={
                        <Button
                            type="primary"
                            size="middle"
                            onClick={handleNewSecretClick}
                            disabled={createButtonDisabled}
                        >
                            Add Secret
                        </Button>
                    }
                    title="Secrets"
                    table={{
                        rowKey: (row) => row.id!,
                        noData: (
                            <FeatureEmptyState
                                slot="rail:domain:security"
                                text="Click ‘Add Secret’ to create your first Secret"
                            />
                        ),
                        hideFooter: false,
                        rowClassName: 'data-view-row-collapsed',
                        className: 'aqe-table-well aqe-table-well-shadow',
                    }}
                >
                    <Table.Column key="alias" className="alias" title="Alias" dataIndex={['data', 'alias']} />

                    <Table.Column
                        align="center"
                        key="type"
                        className="type"
                        title="Type"
                        width={200}
                        dataIndex={['data', 'type']}
                        render={(value) => {
                            return <span>{secretTypeLabels[value]}</span>
                        }}
                    />

                    <Table.Column
                        align="center"
                        key="last-updated-date"
                        className="last-updated-date"
                        title="Last Updated Date"
                        width={160}
                        dataIndex={['data', 'updated_at']}
                        render={(value) => {
                            return <span>{value ? moment(value).format(SHORT_DATE_FORMAT) : null}</span>
                        }}
                    />
                    <Table.Column
                        align="right"
                        className="secrets-actions"
                        key="actions"
                        title="Actions"
                        width={200}
                        dataIndex="data"
                        render={(value: DomainSecret) => {
                            return (
                                <SecretsActionColumnView
                                    onArchiveClick={() => setShowArchiveModal(value)}
                                    onEditClick={() => setEditSecret(value)}
                                />
                            )
                        }}
                    />
                </DataView>

                <DomainSecretsDrawer
                    domainId={id}
                    visible={showCreateSecretModal || editSecret !== undefined}
                    secret={editSecret}
                    onSubmit={handleSubmitSecret}
                    submitDisabled={() => false}
                    onClose={() => {
                        setEditSecret(undefined)
                        setShowCreateSecretModal(false)
                    }}
                />

                <ArchiveSecretModal
                    visible={showArchiveModal !== undefined}
                    alias={showArchiveModal?.alias ?? ''}
                    onCancel={() => {
                        setShowArchiveModal(undefined)
                    }}
                    onOk={handleDeleteSecret}
                />
            </DataViewContext.Provider>
        </>
    )
}
